import React, { useContext, useRef, useState } from 'react';
import { Box, TextField, Typography, IconButton, InputAdornment, CircularProgress } from '@mui/material';
import { WriteButton } from '../../ui';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { TenantAssetContext } from '../TenantAssetContext';
import axiosClient from '../../../api/axiosClient';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp', '.ico'];
const IMAGE_KEY_HINTS = ['image', 'icon', 'art', 'logo', 'pin', 'photo', 'splash', 'ftue'];

function isImageUrl(value: string, label: string): boolean {
  const lower = (value || '').toLowerCase();
  const labelLower = label.toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))) return true;
  if (IMAGE_KEY_HINTS.some((hint) => labelLower.includes(hint))) return true;
  if (lower.includes('/images/') || lower.includes('/icons/') || lower.includes('/assets/')) return true;
  return false;
}

interface Props {
  label: string;
  description?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

const UrlField: React.FC<Props> = ({ label, description, value, onChange, placeholder }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewError, setPreviewError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const tenantAsset = useContext(TenantAssetContext);
  const isImage = isImageUrl(value || '', label);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!tenantAsset) {
      setUploadError('Upload context unavailable');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const buf = await file.arrayBuffer();
      // Convert ArrayBuffer to base64 without overflowing the call stack on large files
      const bytes = new Uint8Array(buf);
      let binary = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
      }
      const contentBase64 = btoa(binary);
      const { data } = await axiosClient.post('/api/tenant-asset/upload', {
        env: tenantAsset.env,
        tenantCode: tenantAsset.tenantCode,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBase64,
      });
      onChange(data.url);
      setPreviewError(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Upload failed';
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#141417', mb: 0.5, fontFamily: 'Manrope, sans-serif' }}>
        {label}
      </Typography>
      {description && <Typography sx={{ fontSize: 11, color: '#6F6F6F', mb: 0.5 }}>{description}</Typography>}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          value={value || ''}
          onChange={(e) => { onChange(e.target.value); setPreviewError(false); }}
          placeholder={placeholder || 'https://...'}
          InputProps={{
            sx: { fontSize: 13, borderRadius: '6px' },
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => window.open(value, '_blank')}
                  title="Open in new tab"
                  sx={{ color: '#0078B3' }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Upload button for image URLs — only when an asset context is present */}
        {isImage && tenantAsset && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <WriteButton
              size="small"
              variant="outlined"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              startIcon={uploading ? <CircularProgress size={12} /> : <CloudUploadIcon sx={{ fontSize: 14 }} />}
              sx={{
                textTransform: 'none', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                borderColor: '#D3D6DC', color: '#141417', borderRadius: '6px', minWidth: 90, height: 38,
                '&:hover': { borderColor: '#FAB306', backgroundColor: '#FAB30608' },
              }}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </WriteButton>
          </>
        )}
      </Box>
      {uploadError && (
        <Typography sx={{ fontSize: 11, color: '#DC2626', mt: 0.5 }}>{uploadError}</Typography>
      )}

      {/* Image preview */}
      {isImage && value && !previewError && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 80, height: 48, borderRadius: '6px', border: '1px solid #EFF0F0',
              overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#FAFAFA', flexShrink: 0,
            }}
          >
            <img
              src={value}
              alt={label}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              onError={() => setPreviewError(true)}
            />
          </Box>
          <Typography sx={{ fontSize: 10, color: '#A1A1A1', wordBreak: 'break-all' }}>
            {value.startsWith('data:') ? 'Uploaded image (base64)' : value.split('/').pop()}
          </Typography>
        </Box>
      )}

      {/* Fallback when image can't load */}
      {isImage && value && previewError && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: '#A1A1A1' }}>
          <ImageIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 11 }}>Image preview unavailable</Typography>
        </Box>
      )}
    </Box>
  );
};

export default UrlField;
